import React from "react";
import { Box, CircleCheck, Truck, Gavel, Users, Percent, MapPin, Star, Globe, House } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalyticsResource } from "../hooks/useAnalyticsResource";
import { BarChart, Card, EmptyState, Kpi, ResourceState } from "../components/shared/AnalyticsWidgets";
import { formatNumber, hasNumber, validRows } from "../utils/analyticsFormat";
import styles from "../OrderAnalyticsPage.module.scss";
import { routeName } from "../components/charts/RoutePieChart";
import RoutesByMode from "../components/directions/RoutesByMode";

const currencies = [
  { key: "kzt", symbol: "₸", color: "blue" },
  { key: "usd", symbol: "$", color: "green" },
  { key: "eur", symbol: "€", color: "violet" },
];

function useFormat() {
  const { i18n } = useTranslation();
  const number = (value, digits = 0) => formatNumber(value, i18n.language, digits);
  const percent = (value) => (hasNumber(value) ? `${number(value, 1)}%` : "—");
  const money = (value, symbol = "₸") => (hasNumber(value) ? `${symbol} ${number(value, 2)}` : "—");
  return { number, percent, money };
}

const modeField = (mode, fields) => fields[mode];

function transportSeries(t, autoKey, airKey) {
  return [
    { name: t("analyticsNew.mode.auto", "Авто"), valueKey: autoKey, color: "var(--blue)" },
    { name: t("analyticsNew.mode.air", "Авиа"), valueKey: airKey, color: "var(--cyan)" },
  ];
}

function percentOf(value, total) {
  return hasNumber(value) && hasNumber(total) && total > 0 ? (value / total) * 100 : null;
}

function selectOverview(general, mode) {
  return {
    total: general?.[modeField(mode, { all: "total", auto: "totalOrders", air: "totalAirOrders" })],
    completed:
      general?.[
        modeField(mode, {
          all: "totalCompleted",
          auto: "totalCompletedAuto",
          air: "totalCompletedAir",
        })
      ],
    onWay:
      general?.[
        modeField(mode, { all: "ontheWayTotal", auto: "ontheWayAuto", air: "ontheWayAir" })
      ],
  };
}

function selectAssignment(data, mode) {
  const countKey = modeField(mode, { all: "count", auto: "autoCount", air: "airCount" });
  const total = [data.throughTender, data.directAssign].reduce(
    (sum, item) => sum + (hasNumber(item?.[countKey]) ? item[countKey] : 0),
    0,
  );
  return {
    countKey,
    total,
    tenderPercentage:
      mode === "all" ? data.throughTender?.percentage : percentOf(data.throughTender?.[countKey], total),
  };
}

export function OverviewPanel({ period, mode, onAskAssistant, assistantPending }) {
  const { t } = useTranslation();
  const { number, percent, money } = useFormat();
  const summary = useAnalyticsResource("summary", period, mode);
  const dynamics = useAnalyticsResource("dynamics", period, mode);
  const assignment = useAnalyticsResource("assignment", period, mode);
  const chartTitle = t("analyticsNew.overview.dynamics", "Количество перевозок за период");
  return (
    <>
      <ResourceState resource={summary}>
        {(data) => {
          const { total, completed, onWay } = selectOverview(data.general, mode);
          return (
            <>
              <div className={`${styles.kpis} ${styles.k3}`}>
              <Kpi
                icon={Box}
                color="blue"
                label={t("analyticsNew.overview.total", "Всего заказов")}
                value={number(total)}
                detail={
                  mode === "all"
                    ? `${t("analyticsNew.overview.auto", "авто")} ${number(data.general?.totalOrders)} · ${t("analyticsNew.overview.air", "авиа")} ${number(data.general?.totalAirOrders)}`
                    : `${t("analyticsNew.only", "только")} ${t(`analyticsNew.mode.${mode}Lower`, mode === "auto" ? "авто" : "авиа")}`
                }
              />
              <Kpi
                icon={CircleCheck}
                color="green"
                label={t("analyticsNew.overview.completed", "Завершено")}
                value={number(completed)}
                detail={`${percent(
                  mode === "all" ? data.general?.totalCompletedPercentage : percentOf(completed, total),
                )} ${t("analyticsNew.overview.ofAll", "от всех заказов")}`}
              />
              <Kpi
                icon={Truck}
                color="cyan"
                label={t("analyticsNew.overview.onWay", "Сейчас в пути")}
                value={number(onWay)}
                detail={t("analyticsNew.overview.onWayHint", "погрузка, дорога и разгрузка")}
              />
              </div>
              <Card
                title={t("analyticsNew.overview.turnover", "Оборот по валютам")}
                hint={t(
                  "analyticsNew.overview.turnoverHint",
                  "Без пересчёта в тенге: суммы в каждой валюте за выбранные даты",
                )}
              >
                <div className={styles.currencyGrid}>
                  {currencies.map(({ key, symbol, color }) => (
                    <div className={styles.currency} key={key}>
                      <span className={`${styles.symbol} ${styles[color]}`}>{symbol}</span>
                      <div>
                        <div className={styles.amount}>
                          {money(
                            mode === "all"
                              ? data.amountByCurrency?.[key]?.sum
                              : data.amountByCurrency?.[key]?.[mode]?.sum,
                            symbol,
                          )}
                        </div>
                        <div className={styles.detail}>
                          {number(
                            mode === "all"
                              ? data.amountByCurrency?.[key]?.count
                              : data.amountByCurrency?.[key]?.[mode]?.count,
                          )}{" "}
                          {t("analyticsNew.orders", "заказов")} · {key.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          );
        }}
      </ResourceState>
      <Card
        title={chartTitle}
        hint={t(
          "analyticsNew.overview.dynamicsHint",
          "Сколько заказов создано в выбранных датах. Шаг графика подстраивается под диапазон: дни, недели или месяцы",
        )}
      >
        <ResourceState resource={dynamics}>
          {(data) => (
            <BarChart
              data={data.data}
              valueKey={modeField(mode, { all: "count", auto: "autoCount", air: "airCount" })}
              series={mode === "all" ? transportSeries(t, "autoCount", "airCount") : undefined}
              color={mode === "air" ? "cyan" : "blue"}
            />
          )}
        </ResourceState>
      </Card>
      <Card
        title={t("analyticsNew.overview.assignment", "Тендер или прямой проброс")}
        hint={t(
          "analyticsNew.overview.assignmentHint",
          "Прямой проброс — логист назначил ТК сразу, без тендера. Доля заказов по способу назначения перевозчика",
        )}
      >
        <ResourceState resource={assignment}>
          {(data) => {
            const { countKey, total, tenderPercentage } = selectAssignment(data, mode);
            return (
              <>
                <div className={styles.split}>
                {[
                  {
                    item: data.throughTender,
                    label: t("analyticsNew.overview.tender", "через тендер"),
                    color: "blue",
                  },
                  {
                    item: data.directAssign,
                    label: t("analyticsNew.overview.direct", "прямой проброс"),
                    color: "violet",
                  },
                ].map(({ item, label, color }) => (
                  <button
                    type="button"
                    className={styles.assignment}
                    key={color}
                    disabled={assistantPending}
                    onClick={() =>
                      onAskAssistant(
                        t(
                          "analyticsNew.assistant.assignmentQuestion",
                          "Сколько прямым пробросом и через тендер?",
                        ),
                        { tool: "orders-by-tender-type" },
                      )
                    }
                  >
                    <div className={styles.assignmentNumber}>{number(item?.[countKey])}</div>
                    <div className={styles.label}>{label}</div>
                    <span className={`${styles.pill} ${styles[color]}`}>
                      {percent(
                        mode === "all" ? item?.percentage : percentOf(item?.[countKey], total),
                      )}{" "}
                      {t("analyticsNew.orders", "заказов")}
                    </span>
                  </button>
                ))}
              </div>
                {hasNumber(tenderPercentage) && (
                  <div className={styles.ratio} aria-hidden="true">
                    <span style={{ width: `${Math.max(0, Math.min(100, tenderPercentage))}%` }} />
                  </div>
                )}
              </>
            );
          }}
        </ResourceState>
      </Card>
    </>
  );
}

export function TendersPanel({ period, mode }) {
  const { t } = useTranslation();
  const { number, percent } = useFormat();
  const resource = useAnalyticsResource("tenders", period, mode);
  const speedTitle = t("analyticsNew.tenders.speed", "Как быстро приходит первое предложение");
  const savingsTitle = t("analyticsNew.tenders.savings", "Сколько экономит торг");
  return (
    <ResourceState resource={resource}>
      {(data) => (
        <>
          <div className={`${styles.kpis} ${styles.k3}`}>
            <Kpi
              icon={Gavel}
              color="blue"
              label={t("analyticsNew.tenders.count", "Тендеров проведено")}
              value={number(
                data.cards?.[
                  modeField(mode, {
                    all: "tendersCount",
                    auto: "tendersCountAuto",
                    air: "tendersCountAir",
                  })
                ],
              )}
              detail={t("analyticsNew.tenders.countHint", "заказ опубликован — ставки получены")}
            />
            <Kpi
              icon={Users}
              color="violet"
              label={t("analyticsNew.tenders.offers", "Предложений на заказ")}
              value={number(
                data.cards?.[
                  modeField(mode, {
                    all: "averageOffersPerTender",
                    auto: "averageOffersPerTenderAuto",
                    air: "averageOffersPerTenderAir",
                  })
                ],
                1,
              )}
              detail={`${t("analyticsNew.tenders.totalOffers", "Всего предложений")}: ${number(
                data.cards?.[
                  modeField(mode, { all: "offersCount", auto: "offersCountAuto", air: "offersCountAir" })
                ],
              )}`}
            />
            <Kpi
              icon={Percent}
              color="green"
              label={t("analyticsNew.tenders.discount", "Торг снижает цену на")}
              value={percent(
                data.cards?.[
                  modeField(mode, {
                    all: "savingsPercent",
                    auto: "savingsPercentAuto",
                    air: "savingsPercentAir",
                  })
                ],
              )}
              detail={t(
                "analyticsNew.tenders.discountHint",
                "выбранная ставка против средней из предложенных",
              )}
            />
          </div>
          <Card
            title={speedTitle}
            hint={t(
              "analyticsNew.tenders.speedHint",
              "Время от публикации заказа до первой ставки любой ТК. Столбики — сколько тендеров попало в каждый интервал",
            )}
          >
            <BarChart
              title={speedTitle}
              data={data.firstOfferTime}
              valueKey={modeField(mode, { all: "count", auto: "autoCount", air: "airCount" })}
              series={mode === "all" ? transportSeries(t, "autoCount", "airCount") : undefined}
              color="pink"
            />
          </Card>
          <Card
            title={savingsTitle}
            hint={t(
              "analyticsNew.tenders.savingsHint",
              "Сравниваем выбранную ставку со средней из всех предложений в том же тендере. Разница — заслуга конкуренции между ТК",
            )}
          >
            <BarChart
              title={savingsTitle}
              data={data.savingsByMonth}
              valueKey={modeField(mode, {
                all: "savingsPercent",
                auto: "autoSavingsPercent",
                air: "airSavingsPercent",
              })}
              series={
                mode === "all"
                  ? transportSeries(t, "autoSavingsPercent", "airSavingsPercent")
                  : undefined
              }
              color="violet"
              percent
            />
          </Card>
        </>
      )}
    </ResourceState>
  );
}

export function CarriersPanel({ period, mode = "all", onAskAssistant, assistantPending }) {
  const { t } = useTranslation();
  const { number, money } = useFormat();
  const resource = useAnalyticsResource("carriers", period, mode);
  return (
    <Card
      title={t("analyticsNew.carriers.title", "Сколько перевёз каждый перевозчик")}
      hint={t(
        "analyticsNew.carriers.hint",
        "Завершённые перевозки за выбранные даты. Суммы указаны отдельно в каждой валюте, без конвертации и суммирования",
      )}
    >
      <ResourceState resource={resource}>
        {(data) =>
          validRows(data).length ? (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">
                      <span className={styles.srOnly}>{t("analyticsNew.carriers.rank", "Номер")}</span>
                    </th>
                    <th scope="col">{t("analyticsNew.carriers.name", "Перевозчик")}</th>
                    <th scope="col">{t("analyticsNew.carriers.mode", "Вид")}</th>
                    <th scope="col" className={styles.numeric}>
                      {t("analyticsNew.carriers.orders", "Перевозок")}
                    </th>
                    {currencies.map(({ key, symbol }) => (
                      <th scope="col" className={styles.numeric} key={key}>
                        {t("analyticsNew.carriers.amount", "Сумма")} {symbol}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validRows(data).map((row, index) => (
                    <tr key={row.carrierId || index}>
                      <td className={styles.rank}>{index + 1}</td>
                      <th scope="row" className={styles.carrierName}>
                        {typeof row.carrierName === "string" ? (
                          <button
                            type="button"
                            className={styles.assistantLink}
                            disabled={assistantPending}
                            onClick={() =>
                              onAskAssistant(
                                `${t("analyticsNew.assistant.companyPrefix", "Расскажи про ")}${row.carrierName}`,
                                { tool: "company-info", companyName: row.carrierName },
                              )
                            }
                          >
                            {row.carrierName}
                          </button>
                        ) : (
                          "—"
                        )}
                      </th>
                      <td className={styles.carrierModes}>
                        {[
                          ["auto", "autoOrdersCount", t("analyticsNew.overview.auto", "авто")],
                          ["air", "airOrdersCount", t("analyticsNew.overview.air", "авиа")],
                        ].map(([mode, countKey, label]) =>
                          hasNumber(row[countKey]) && row[countKey] > 0 ? (
                            <span className={styles.carrierModeTag} data-mode={mode} key={mode}>
                              {label} {number(row[countKey])}
                            </span>
                          ) : null,
                        )}
                      </td>
                      <td className={styles.numeric}>{number(row.ordersCount)}</td>
                      <td className={styles.numeric}>{money(row.amountKzt)}</td>
                      <td className={styles.numeric}>{money(row.amountUsd, "$")}</td>
                      <td className={styles.numeric}>{money(row.amountEur, "€")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState />
          )
        }
      </ResourceState>
    </Card>
  );
}

export function DirectionsPanel({ period, mode = "all", onAskAssistant, assistantPending }) {
  const { t } = useTranslation();
  const { number, money } = useFormat();
  const resource = useAnalyticsResource("directions", period, mode);
  return (
    <ResourceState resource={resource}>
      {(data) => {
        const routes = validRows(data.topRoutes);
        const countKey = modeField(mode, { all: "count", auto: "autoCount", air: "airCount" });
        const mostFrequent = mode === "all" ? data.mostFrequent : routes
          .filter((row) => hasNumber(row[countKey]) && row[countKey] > 0)
          .sort((a, b) => b[countKey] - a[countKey])[0];
        const directionCount = (group) => mode === "all" ? group?.count : group?.byMode?.[countKey];
        const turnoverKey = modeField(mode, {
          all: "turnoverKzt", auto: "turnoverKztAuto", air: "turnoverKztAir",
        });
        return (
          <>
            <div className={styles.kpis}>
              <Kpi
                icon={MapPin}
                color="blue"
                label={t("analyticsNew.directions.count", "Направлений в работе")}
                value={number(mode === "all" ? data.directionsInWork : data.directionsInWorkByMode?.[countKey])}
                detail={t("analyticsNew.directions.countHint", "маршруты с заказами за период")}
              />
              {mode !== "all" && (
                <Kpi
                  icon={Star}
                  color="orange"
                  label={t("analyticsNew.directions.frequent", "Самое частое")}
                  value={
                    hasNumber(mostFrequent?.[countKey])
                      ? `${number(mostFrequent[countKey])} ${t("analyticsNew.shortOrders", "зак.")}`
                      : "—"
                  }
                  detail={routeName(mostFrequent, " — ")}
                />
              )}
              <Kpi
                icon={Globe}
                color="violet"
                label={t("analyticsNew.directions.international", "Международных")}
                value={number(directionCount(data.international))}
                detail={`${t("analyticsNew.directions.turnover", "оборот")} ${money(data.international?.[turnoverKey], "KZT")}`}
              />
              <Kpi
                icon={House}
                color="green"
                label={t("analyticsNew.directions.domestic", "По Казахстану")}
                value={number(directionCount(data.domestic))}
                detail={`${t("analyticsNew.directions.turnover", "оборот")} ${money(data.domestic?.[turnoverKey], "KZT")}`}
              />
            </div>
            <RoutesByMode
              routesByMode={data.routesByMode}
              topRoutes={routes}
              mode={mode}
              onAskAssistant={onAskAssistant}
              assistantPending={assistantPending}
            />
          </>
        );
      }}
    </ResourceState>
  );
}
